import { CommonModule } from '@angular/common'
import { Component, OnDestroy, OnInit } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { ButtonModule } from 'primeng/button'
import { SelectModule } from 'primeng/select'
import { SkeletonModule } from 'primeng/skeleton'
import { TableModule } from 'primeng/table'
import { TruncatedCopyComponent } from '../truncated-copy/truncated-copy.component'
import { OutboundSyncFilters, OutboundSyncItem, OutboundSyncSource, OutboundSyncState, OutboundSyncsService } from '@/service/outbound-syncs.service'
import { Subscription } from 'rxjs'

type SortField = keyof Pick<OutboundSyncItem, 'source' | 'state' | 'syncId' | 'entityReference' | 'queuedAt' | 'nextActionAt' | 'attemptCount' | 'lastError'> | 'entityTypes'

@Component({
  selector: 'pending-syncs',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, SelectModule, SkeletonModule, TableModule, TruncatedCopyComponent],
  templateUrl: './pending-syncs.component.html',
  styleUrl: './pending-syncs.component.scss',
})
export class PendingSyncsComponent implements OnInit, OnDestroy {
  readonly sources = [{ label: 'All sources', value: 'all' }, { label: 'Atomic', value: 'atomic' }, { label: 'Bulk', value: 'bulk' }]
  readonly states = [{ label: 'All states', value: 'all' }, { label: 'Queued', value: 'queued' }, { label: 'Processing', value: 'processing' }]
  filters: OutboundSyncFilters = { source: 'all', state: 'all' }
  rows: OutboundSyncItem[] = []
  loading = true
  manualRefreshing = false
  error: string | null = null
  liveState: 'connecting' | 'connected' | 'unavailable' = 'connecting'
  sortField: SortField = 'queuedAt'
  sortOrder: 1 | -1 = -1

  private cursors: { atomic: string | null; bulk: string | null } = { atomic: null, bulk: null }
  private destroyed = false
  private request: AbortController | null = null
  private readonly subscriptions = new Subscription()

  constructor(private readonly service: OutboundSyncsService) {}

  ngOnInit(): void {
    void this.refresh()
    this.service.startStatusMonitor()
    this.subscriptions.add(this.service.status$.subscribe((status) => this.liveState = status.live))
    this.subscriptions.add(this.service.changes$.subscribe(() => void this.refresh()))
  }

  ngOnDestroy(): void {
    this.destroyed = true
    this.request?.abort()
    this.subscriptions.unsubscribe()
  }

  get sortedRows(): OutboundSyncItem[] {
    return [...this.rows].sort((left, right) => this.compare(left[this.sortField], right[this.sortField]) * this.sortOrder)
  }

  async refresh(showRefreshIndicator = false): Promise<void> {
    if (showRefreshIndicator) this.manualRefreshing = true
    try {
      await this.loadAll()
    } finally {
      if (showRefreshIndicator) this.manualRefreshing = false
    }
  }

  async onFiltersChanged(): Promise<void> {
    await this.refresh()
  }

  sort(field: SortField): void {
    this.sortOrder = this.sortField === field ? (this.sortOrder === 1 ? -1 : 1) : 1
    this.sortField = field
  }

  sortIcon(field: SortField): string {
    return this.sortField !== field ? 'pi pi-sort-alt' : this.sortOrder === 1 ? 'pi pi-sort-amount-up-alt' : 'pi pi-sort-amount-down'
  }

  date(value: string | null): string {
    return value ? new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'medium' }).format(new Date(value)) : '—'
  }

  text(value: string | null): string { return value?.replace(/[\r\n]+/g, ' ').slice(0, 500) || '—' }
  entityTypes(row: OutboundSyncItem): string { return row.entityTypes.map((type) => type.replaceAll('_', ' ')).join(', ') }
  entityReferences(row: OutboundSyncItem): string {
    return row.entityReferences.length ? row.entityReferences.join(', ') : this.text(row.entityReference)
  }
  entityCount(row: OutboundSyncItem, type: string): number | null { return row.entityCounts[type] ?? null }

  private async loadAll(): Promise<void> {
    this.request?.abort()
    const request = this.request = new AbortController()
    this.cursors = { atomic: null, bulk: null }
    // Keep populated tables stable during filter, manual, and live refreshes.
    // Skeleton rows are only useful while there is no result to display yet.
    this.loading = this.rows.length === 0
    this.error = null
    try {
      const items: OutboundSyncItem[] = []
      let cursors: { atomic: string | null; bulk: string | null } | undefined
      do {
        const page = await this.service.list(this.filters, cursors, request.signal)
        items.push(...page.items)
        cursors = page.nextCursors
        // Show useful results as soon as the first page arrives; remaining
        // atomic/bulk pages continue to merge in without blocking the table.
        if (!this.destroyed) {
          this.rows = this.mergeByQueuedTime(items)
          this.loading = false
        }
      } while ((cursors.atomic || cursors.bulk) && !request.signal.aborted)
      if (this.destroyed) return
      this.cursors = { atomic: null, bulk: null }
    } catch (error) {
      if (!this.destroyed && !(error instanceof DOMException && error.name === 'AbortError')) {
        this.error = error instanceof Error ? error.message : 'Failed to load pending outbound syncs.'
      }
    } finally {
      if (!this.destroyed && this.request === request) this.loading = false
    }
  }

  private mergeByQueuedTime(items: OutboundSyncItem[]): OutboundSyncItem[] {
    const unique = new Map<string, OutboundSyncItem>()
    items.forEach((item) => unique.set(`${item.source}:${item.syncId}`, item))
    return [...unique.values()].sort((a, b) => (Date.parse(b.queuedAt ?? '') || 0) - (Date.parse(a.queuedAt ?? '') || 0))
  }

  private compare(left: unknown, right: unknown): number {
    const a = left ?? ''
    const b = right ?? ''
    return typeof a === 'number' && typeof b === 'number' ? a - b : (Array.isArray(a) ? a.join(', ') : String(a)).localeCompare(Array.isArray(b) ? b.join(', ') : String(b))
  }
}
